import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Brain, Heart, Zap, TrendingUp, Database, Activity } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import BiometricHistoryChart from './BiometricHistoryChart';
import MeditationProgressTracker from './MeditationProgressTracker';

interface SpiritualData {
  sessions: any[];
  biometrics: any[];
  progress: any;
  profile: any;
}

export default function SpiritualMLDashboard() {
  const [data, setData] = useState<SpiritualData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeModel, setActiveModel] = useState('eeg');

  useEffect(() => {
    loadSpiritualData();
  }, []);

  const loadSpiritualData = async () => {
    try {
      setLoading(true);
      
      // Load historical data
      const { data: historicalData } = await supabase.functions.invoke('spiritual-data-manager', {
        body: { 
          action: 'get_historical_data',
          userId: 'demo-user-123'
        }
      });

      // Load spiritual profile
      const { data: profileData } = await supabase.functions.invoke('spiritual-data-manager', {
        body: { 
          action: 'get_spiritual_profile',
          userId: 'demo-user-123'
        }
      });

      setData({
        sessions: historicalData?.sessions || [],
        biometrics: historicalData?.biometrics || [],
        progress: historicalData?.progress || {},
        profile: profileData || {}
      });
    } catch (error) {
      console.error('Error loading spiritual data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveMeditationSession = async () => {
    try {
      await supabase.functions.invoke('spiritual-data-manager', {
        body: {
          action: 'save_meditation_session',
          userId: 'demo-user-123',
          data: {
            type: 'mindfulness',
            duration: 20,
            quality: 85 + Math.random() * 15,
            coherence: 7 + Math.random() * 3,
            brainwaveState: 'alpha',
            depth: 80 + Math.random() * 20
          }
        }
      });
      
      // Reload data after saving
      await loadSpiritualData();
    } catch (error) {
      console.error('Error saving session:', error);
    }
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <Database className="w-8 h-8 animate-spin mx-auto text-purple-400" />
          <div className="text-sm text-muted-foreground">Loading spiritual data...</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-muted-foreground">
        No spiritual data available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Spiritual ML Dashboard</h2>
          <p className="text-gray-400">Advanced consciousness analysis with neural networks</p>
        </div>
        <Button onClick={saveMeditationSession} className="bg-purple-600 hover:bg-purple-700">
          <Activity className="w-4 h-4 mr-2" />
          Start Session
        </Button>
      </div>

      <Tabs value={activeModel} onValueChange={setActiveModel} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="eeg">EEG Analysis</TabsTrigger>
          <TabsTrigger value="coherence">Heart Coherence</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="eeg" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-black/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-purple-300 flex items-center gap-2">
                  <Brain className="w-5 h-5" />
                  EEG Pattern Classification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Neural Network Accuracy</span>
                    <span className="text-green-400">94.2%</span>
                  </div>
                  <Progress value={94.2} className="h-2" />
                </div>
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                  Deep Focus State Detected
                </Badge>
              </CardContent>
            </Card>

            <BiometricHistoryChart 
              data={data.biometrics} 
              metric="brainwaves"
            />
          </div>
        </TabsContent>

        <TabsContent value="coherence" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-black/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-purple-300 flex items-center gap-2">
                  <Heart className="w-5 h-5" />
                  Heart Coherence Prediction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-300">Coherence Score</span>
                    <span className="text-green-400">{data.profile.consciousnessScore?.toFixed(1) || '0.0'}%</span>
                  </div>
                  <Progress value={data.profile.consciousnessScore || 0} className="h-2" />
                </div>
                <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                  Optimal Coherence
                </Badge>
              </CardContent>
            </Card>

            <BiometricHistoryChart 
              data={data.biometrics} 
              metric="coherence"
            />
          </div>
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          <MeditationProgressTracker
            sessions={data.sessions}
            totalHours={data.progress.totalHours || 0}
            avgQuality={data.progress.avgQuality || 0}
            consciousnessLevel={data.progress.consciousnessLevel || 1}
          />
        </TabsContent>

        <TabsContent value="profile" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-black/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-purple-300">Spiritual Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Consciousness Level</span>
                    <Badge>{data.profile.spiritualLevel || 1}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Meditation Hours</span>
                    <span className="text-purple-400">{data.profile.meditationHours?.toFixed(1) || '0.0'}h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Aura Color</span>
                    <Badge style={{ backgroundColor: data.profile.auraColor }}>
                      {data.profile.auraColor || 'Unknown'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-black/50 border-purple-500/30">
              <CardHeader>
                <CardTitle className="text-purple-300">Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(data.profile.recommendations || []).map((rec: string, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 rounded bg-purple-500/10">
                      <Zap className="w-4 h-4 text-purple-400" />
                      <span className="text-sm text-gray-300">{rec}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}